/**
 * SponsorsPage — /sponsors
 *
 * Full cinematic red-carpet premiere scene featuring the real sponsor
 * family toon portraits positioned with vanishing-point depth scaling.
 *
 * Depth order (back → front):
 *   1. Marco Valenciano Family  — farthest, smallest, center
 *   2. Carlo Valenciano Family  — mid-left
 *   3. Obligacion Family        — mid-right
 *   4. Manny                    — foreground center, largest
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Real sponsor portrait assets ──────────────────────────────────────────────
const CARPET_SPONSORS = [
  {
    id: 'marco',
    name: 'The Marco Valenciano Family',
    img: 'https://customer-assets.emergentagent.com/job_96f73dc0-b292-41dd-a366-8b41de2ebdcc/artifacts/1aaaf6hj_Valenciano%20Marco%20family%20toon%202.png',
    bottom: '42%', cx: '50%', width: 'clamp(90px,14vw,145px)',
    zIndex: 20, delay: '0.9s', spotW: '260%',
  },
  {
    id: 'carlo',
    name: 'The Carlo Valenciano Family',
    img: 'https://customer-assets.emergentagent.com/job_96f73dc0-b292-41dd-a366-8b41de2ebdcc/artifacts/ohke4hd7_Valenciano%20Carlo%20family%20toon2.2.1.png',
    bottom: '24%', cx: '22%', width: 'clamp(130px,20vw,210px)',
    zIndex: 25, delay: '0.3s', spotW: '300%',
  },
  {
    id: 'obligacion',
    name: 'The Obligacion Family',
    img: 'https://customer-assets.emergentagent.com/job_96f73dc0-b292-41dd-a366-8b41de2ebdcc/artifacts/5mahi4wp_Obligacion%20Familiy%20Toon2.png',
    bottom: '24%', cx: '78%', width: 'clamp(130px,20vw,210px)',
    zIndex: 25, delay: '0.6s', spotW: '300%',
  },
  {
    id: 'manny',
    name: 'Manny',
    img: 'https://customer-assets.emergentagent.com/job_96f73dc0-b292-41dd-a366-8b41de2ebdcc/artifacts/wugouiez_Manny%20toon3.png',
    bottom: '3%', cx: '50%', width: 'clamp(160px,26vw,270px)',
    zIndex: 30, delay: '0s', spotW: '340%',
  },
];

// ── Stanchion rows ────────────────────────────────────────────────────────────
const ROWS = [
  { y: 91, ty: 62, lx: 5,  rx: 95, h: 220, br: 11, pw: 9,  bw: 34 },
  { y: 79, ty: 57, lx: 12, rx: 88, h: 168, br: 8,  pw: 7,  bw: 27 },
  { y: 69, ty: 52, lx: 19, rx: 81, h: 128, br: 7,  pw: 6,  bw: 22 },
  { y: 60, ty: 47, lx: 25, rx: 75, h: 97,  br: 5,  pw: 4.5,bw: 18 },
  { y: 53, ty: 43, lx: 30, rx: 70, h: 74,  br: 4,  pw: 3.5,bw: 14 },
  { y: 47, ty: 39, lx: 34, rx: 66, h: 57,  br: 3,  pw: 2.5,bw: 11 },
  { y: 42, ty: 36, lx: 37, rx: 63, h: 44,  br: 2.5,pw: 2,  bw: 9  },
];

// ── Camera flash positions (paparazzi both flanks) ────────────────────────────
const FLASHES = [
  { x:2,  y:58, d:0.0, s:38 }, { x:7,  y:70, d:1.3, s:32 }, { x:4,  y:48, d:2.7, s:44 },
  { x:10, y:63, d:0.8, s:36 }, { x:1,  y:80, d:3.5, s:30 }, { x:8,  y:55, d:1.9, s:42 },
  { x:93, y:61, d:0.5, s:40 }, { x:97, y:72, d:2.1, s:34 }, { x:90, y:53, d:3.8, s:46 },
  { x:95, y:67, d:1.1, s:38 }, { x:99, y:83, d:0.3, s:28 }, { x:92, y:46, d:4.2, s:42 },
  { x:3,  y:86, d:2.4, s:32 }, { x:6,  y:41, d:4.6, s:36 }, { x:96, y:88, d:0.9, s:38 },
  { x:89, y:44, d:3.2, s:40 }, { x:11, y:90, d:1.7, s:30 }, { x:98, y:52, d:2.8, s:44 },
  { x:2,  y:74, d:5.1, s:36 }, { x:9,  y:35, d:4.0, s:32 },
  // Extra bursts near sponsor groups
  { x:18, y:55, d:1.4, s:30 }, { x:22, y:68, d:3.0, s:26 }, { x:78, y:55, d:0.7, s:30 },
  { x:82, y:68, d:2.5, s:26 }, { x:44, y:42, d:1.8, s:24 }, { x:56, y:42, d:3.3, s:24 },
];

// ── Sponsor card (Section 2) ───────────────────────────────────────────────────
function SponsorCard({ sponsor }) {
  return (
    <div
      data-testid={`sponsor-card-${sponsor.id}`}
      style={{
        background: 'rgba(20,15,8,0.72)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1.5px solid rgba(200,170,60,0.35)',
        borderRadius: 16,
        padding: '22px 20px',
        boxShadow: [
          'inset 0 1.5px 0 rgba(255,255,255,0.09)',
          'inset 0 -1px 0 rgba(200,170,60,0.10)',
          '0 8px 28px rgba(0,0,0,0.30)',
        ].join(', '),
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: sponsor.notes ? 14 : 0 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 10, flexShrink: 0,
          background: 'rgba(200,170,60,0.10)',
          border: '1px solid rgba(200,170,60,0.28)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Heart size={18} color="rgba(212,168,83,0.58)" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{
            fontFamily: "'Cinzel',serif", fontSize: 13, fontWeight: 700,
            color: 'rgba(255,228,100,0.97)', margin: '0 0 7px',
            letterSpacing: '0.4px',
            textShadow: '0 1px 8px rgba(212,168,83,0.40)',
            wordBreak: 'break-word',
          }}>{sponsor.name}</p>
          <span style={{
            display: 'inline-block',
            padding: '2px 10px', borderRadius: 20,
            background: 'rgba(200,170,60,0.16)',
            border: '1px solid rgba(200,170,60,0.32)',
            fontFamily: "'Cinzel',serif",
            fontSize: 8, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.9px',
            color: 'rgba(212,168,83,0.85)',
          }}>{sponsor.type}</span>
        </div>
      </div>
      {sponsor.notes && (
        <p style={{
          fontFamily: "'Cormorant Garamond',serif",
          fontSize: 13, fontStyle: 'italic',
          color: 'rgba(255,255,255,0.68)',
          margin: 0, lineHeight: 1.55,
          borderTop: '1px solid rgba(200,170,60,0.14)',
          paddingTop: 12,
        }}>{sponsor.notes}</p>
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function SponsorsPage() {
  const navigate  = useNavigate();
  const cardsRef  = useRef(null);
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/public/sponsor-list`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data) && data.length > 0) setSponsors(data); })
      .catch(() => {});
  }, []);

  // Ceiling grid lights converging to vanishing point
  const ceilingDots = useMemo(() => {
    const dots = [];
    for (let row = 0; row < 10; row++) {
      const t   = row / 9;
      const y   = 3 + t * 27;
      const spd = 8 + t * 41;
      const n   = Math.round(4 + t * 24);
      const sz  = 1.2 + t * 3.8;
      const op  = 0.25 + t * 0.65;
      for (let d = 0; d < n; d++) {
        const x = n === 1 ? 50 : 50 - spd + (d / (n - 1)) * spd * 2;
        dots.push({ x, y, sz, op, id: `${row}-${d}`, dl: (d % 4) * 0.4 });
      }
    }
    return dots;
  }, []);

  // SVG velvet rope paths
  const ropes = useMemo(() => {
    const segs = [];
    for (let i = 0; i < ROWS.length - 1; i++) {
      const a = ROWS[i], b = ROWS[i + 1];
      const midY = (a.ty + b.ty) / 2 + 4.5;
      segs.push(`M ${a.lx} ${a.ty} Q ${(a.lx + b.lx) / 2} ${midY} ${b.lx} ${b.ty}`);
      segs.push(`M ${a.rx} ${a.ty} Q ${(a.rx + b.rx) / 2} ${midY} ${b.rx} ${b.ty}`);
    }
    return segs;
  }, []);

  const GOLD = 'linear-gradient(180deg,#FFE88A 0%,#D4A820 28%,#8B6810 58%,#C9A030 78%,#FFE88A 100%)';

  const renderStanchion = (xPct, yPct, row) => (
    <div style={{
      position: 'absolute',
      left: `${xPct}%`, top: `${yPct}%`,
      transform: 'translate(-50%,-100%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      height: row.h, zIndex: 10, pointerEvents: 'none',
    }}>
      <div style={{ width: row.br * 2, height: row.br * 2, borderRadius: '50%', background: GOLD, flexShrink: 0,
        boxShadow: `0 0 ${row.br * 2}px rgba(212,168,83,0.70)` }} />
      <div style={{ width: row.pw, flex: 1, background: GOLD, boxShadow: `0 0 ${row.pw}px rgba(212,168,83,0.25)` }} />
      <div style={{ width: row.bw, height: row.pw * 1.8, background: GOLD, flexShrink: 0 }} />
    </div>
  );

  const renderSparkles = (xPct, yPct, offset) =>
    [0, 1, 2, 3].map(si => (
      <div key={si} style={{
        position: 'absolute',
        left: `${xPct + (si - 1.5) * 1.1}%`, top: `${yPct}%`,
        width: 3 + si, height: 3 + si, borderRadius: '50%',
        background: 'rgba(255,215,80,0.9)',
        animation: `gspark ${1.1 + si * 0.35}s ease-out ${offset + si * 0.18}s infinite`,
        transform: 'translate(-50%,0)', zIndex: 9, pointerEvents: 'none',
      }} />
    ));

  // Sponsor figure on the carpet
  const renderSponsor = (s) => (
    <div key={s.id} style={{
      position: 'absolute',
      bottom: s.bottom,
      left: s.cx,
      transform: 'translateX(-50%)',
      width: s.width,
      zIndex: s.zIndex,
      pointerEvents: 'none',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
    }}>
      {/* Spotlight cone from above */}
      <div style={{
        position: 'absolute',
        bottom: '80%', left: '50%',
        transform: 'translateX(-50%)',
        width: s.spotW, height: '120px',
        background: 'linear-gradient(to bottom, transparent 0%, rgba(255,238,160,0.06) 60%, rgba(255,238,160,0.14) 100%)',
        clipPath: 'polygon(30% 0%, 70% 0%, 100% 100%, 0% 100%)',
        pointerEvents: 'none',
        animation: 'spotPulse 3.5s ease-in-out infinite',
        animationDelay: s.delay,
      }} />

      {/* Portrait image */}
      <div style={{ position: 'relative', width: '100%' }}>
        <img
          src={s.img}
          alt={s.name}
          style={{
            width: '100%',
            display: 'block',
            mixBlendMode: 'lighten',
            filter: 'drop-shadow(0 4px 18px rgba(212,168,83,0.35)) drop-shadow(0 0 30px rgba(255,240,180,0.18))',
          }}
        />
        {/* Foot-to-carpet blend */}
        <div style={{
          position: 'absolute',
          bottom: 0, left: 0, right: 0,
          height: '18%',
          background: 'linear-gradient(to bottom, transparent, rgba(140,0,0,0.55))',
          pointerEvents: 'none',
        }} />
        {/* Side vignette to blend black edges */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(ellipse 80% 90% at 50% 50%, transparent 55%, rgba(4,2,0,0.55) 100%)',
          pointerEvents: 'none',
        }} />
      </div>

      {/* Elegant name plaque */}
      <div style={{
        marginTop: 4,
        padding: '5px 14px',
        background: 'linear-gradient(135deg, rgba(12,8,2,0.92), rgba(22,15,4,0.92))',
        border: '1px solid rgba(212,168,83,0.58)',
        borderRadius: 3,
        boxShadow: '0 2px 12px rgba(0,0,0,0.60), inset 0 1px 0 rgba(255,220,80,0.12)',
        textAlign: 'center',
        position: 'relative',
      }}>
        {/* Corner diamonds */}
        <span style={{ position:'absolute', top:3, left:5, fontSize:6, color:'rgba(212,168,83,0.55)' }}>✦</span>
        <span style={{ position:'absolute', top:3, right:5, fontSize:6, color:'rgba(212,168,83,0.55)' }}>✦</span>
        <p style={{
          fontFamily: "'Cinzel', serif",
          fontSize: 'clamp(7px,1.1vw,11px)',
          fontWeight: 700,
          letterSpacing: '1.5px',
          color: 'rgba(255,225,100,0.95)',
          margin: 0,
          textShadow: '0 0 10px rgba(212,168,83,0.55)',
          whiteSpace: 'nowrap',
        }}>{s.name}</p>
      </div>
    </div>
  );

  return (
    <div style={{ position: 'relative', background: '#080306' }}>

      {/* ── Keyframes ──────────────────────────────────────────────────────── */}
      <style>{`
        @keyframes camFlash {
          0%,86%,100% { opacity:0; }
          87%,88%     { opacity:0.98; }
          90%          { opacity:0; }
        }
        @keyframes gspark {
          0%,100% { opacity:0; transform:translate(-50%,0) scale(0); }
          20%     { opacity:1; transform:translate(-50%,-9px) scale(1); }
          65%     { opacity:0.5; transform:translate(-50%,-18px) scale(0.7); }
          85%     { opacity:0; transform:translate(-50%,-25px) scale(0.3); }
        }
        @keyframes ceilPulse {
          0%,100% { opacity: var(--op-hi); }
          50%     { opacity: var(--op-lo); }
        }
        @keyframes spotPulse {
          0%,100% { opacity:0.8; }
          50%     { opacity:1; }
        }
        @keyframes eighty {
          0%,100% {
            text-shadow: 0 0 20px rgba(255,215,0,0.9), 0 0 50px rgba(212,168,83,0.60),
                         0 0 80px rgba(200,120,0,0.30);
          }
          50% {
            text-shadow: 0 0 30px rgba(255,225,50,1), 0 0 70px rgba(212,168,83,0.80),
                         0 0 110px rgba(200,120,0,0.45);
          }
        }
        @keyframes gemPulse {
          0%,100% { box-shadow: 0 0 6px rgba(200,0,0,0.80), inset 0 0 6px rgba(255,100,100,0.55); }
          50%     { box-shadow: 0 0 14px rgba(220,0,0,1),   inset 0 0 10px rgba(255,120,120,0.70); }
        }
        @keyframes limoGlow {
          0%,100% { box-shadow: 0 4px 20px rgba(0,0,0,0.80), 0 0 8px rgba(212,168,83,0.10); }
          50%     { box-shadow: 0 4px 20px rgba(0,0,0,0.80), 0 0 16px rgba(212,168,83,0.28); }
        }
        @keyframes scrollCue {
          0%,100% { transform:translateX(-50%) translateY(0); opacity:0.6; }
          50%     { transform:translateX(-50%) translateY(5px); opacity:1; }
        }
        @keyframes heroBtnPulse {
          0%,100% { box-shadow: 0 0 12px rgba(212,168,83,0.40), inset 0 1px 0 rgba(255,220,80,0.15); }
          50%     { box-shadow: 0 0 28px rgba(212,168,83,0.70), inset 0 1px 0 rgba(255,220,80,0.25); }
        }
      `}</style>

      {/* ── SECTION 1: Cinematic red carpet ──────────────────────────────────── */}
      <div style={{ position: 'relative', height: '100vh', overflow: 'hidden' }}>

        {/* Deep background wall — dark art deco brown */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'radial-gradient(ellipse 100% 60% at 50% 0%, #1C1006 0%, #0A0604 55%, #050203 100%)',
          zIndex: 1,
        }} />

        {/* Art deco gold geometric panels — left */}
        {[
          { l:'0%', t:'15%', w:'14%', h:'55%', angle:'-6deg' },
          { l:'2%', t:'10%', w:'10%', h:'45%', angle:'-4deg' },
          { l:'6%', t:'20%', w:'7%',  h:'38%', angle:'-3deg' },
        ].map((p, i) => (
          <div key={`ldp-${i}`} style={{
            position: 'absolute', left: p.l, top: p.t,
            width: p.w, height: p.h,
            background: `linear-gradient(${p.angle}, rgba(212,168,83,0.06) 0%, rgba(212,168,83,0.12) 50%, rgba(212,168,83,0.04) 100%)`,
            border: `0.5px solid rgba(212,168,83,${0.12 - i * 0.02})`,
            transform: `skewX(${p.angle})`,
            zIndex: 2, pointerEvents: 'none',
          }} />
        ))}

        {/* Art deco panels — right */}
        {[
          { r:'0%', t:'15%', w:'14%', h:'55%', angle:'6deg' },
          { r:'2%', t:'10%', w:'10%', h:'45%', angle:'4deg' },
          { r:'6%', t:'20%', w:'7%',  h:'38%', angle:'3deg' },
        ].map((p, i) => (
          <div key={`rdp-${i}`} style={{
            position: 'absolute', right: p.r, top: p.t,
            width: p.w, height: p.h,
            background: `linear-gradient(${p.angle}, rgba(212,168,83,0.04) 0%, rgba(212,168,83,0.12) 50%, rgba(212,168,83,0.06) 100%)`,
            border: `0.5px solid rgba(212,168,83,${0.12 - i * 0.02})`,
            transform: `skewX(-${p.angle})`,
            zIndex: 2, pointerEvents: 'none',
          }} />
        ))}

        {/* Grand arch frame */}
        <div style={{
          position: 'absolute', left: '28%', right: '28%', top: '8%', height: '60%',
          border: '1px solid rgba(212,168,83,0.18)',
          borderBottom: 'none',
          borderRadius: '50% 50% 0 0 / 30% 30% 0 0',
          zIndex: 2, pointerEvents: 'none',
          boxShadow: 'inset 0 0 40px rgba(212,168,83,0.04)',
        }} />

        {/* Ceiling grid lights */}
        {ceilingDots.map(dot => (
          <div key={dot.id} style={{
            position: 'absolute',
            left: `${dot.x}%`, top: `${dot.y}%`,
            width: dot.sz, height: dot.sz, borderRadius: '50%',
            background: `rgba(255,215,80,${dot.op})`,
            boxShadow: `0 0 ${dot.sz * 2.5}px rgba(255,215,80,${dot.op * 0.7})`,
            transform: 'translate(-50%,-50%)',
            '--op-hi': dot.op, '--op-lo': dot.op * 0.35,
            animation: `ceilPulse ${1.8 + dot.dl * 2}s ease-in-out ${dot.dl}s infinite`,
            pointerEvents: 'none', zIndex: 3,
          }} />
        ))}

        {/* ── Golden "80" backdrop ──────────────────────────────────────────── */}
        <div style={{
          position: 'absolute', top: '14%', left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex', alignItems: 'center', gap: '6px',
          zIndex: 8, pointerEvents: 'none',
        }}>
          {/* Gold halo behind 80 */}
          <div style={{
            position: 'absolute',
            width: '100%', height: '100%',
            background: 'radial-gradient(ellipse 80% 70% at 50% 50%, rgba(212,168,83,0.18) 0%, transparent 70%)',
            top: 0, left: 0,
          }} />
          {['8','0'].map((digit, di) => (
            <div key={di} style={{ position: 'relative' }}>
              {/* Outer gold ring */}
              <div style={{
                width: 'clamp(48px,10vw,96px)',
                height: 'clamp(48px,10vw,96px)',
                borderRadius: '50%',
                border: '3px solid rgba(212,168,83,0.88)',
                background: 'radial-gradient(circle at 35% 30%, rgba(255,235,120,0.15), rgba(100,70,10,0.28))',
                boxShadow: [
                  '0 0 22px rgba(212,168,83,0.55)',
                  '0 0 50px rgba(180,120,0,0.25)',
                  'inset 0 1px 0 rgba(255,230,80,0.35)',
                ].join(','),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                position: 'relative', overflow: 'visible',
              }}>
                {/* Digit */}
                <span style={{
                  fontFamily: "'Cinzel', serif",
                  fontSize: 'clamp(26px,5.5vw,56px)',
                  fontWeight: 900,
                  background: 'linear-gradient(180deg, #FFF5A0 0%, #D4A820 38%, #8B6410 62%, #C9A020 82%, #FFF0A0 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  animation: 'eighty 2.8s ease-in-out infinite',
                  animationDelay: `${di * 0.4}s`,
                  lineHeight: 1,
                }}>{digit}</span>

                {/* Ruby gems on ring */}
                {[0,1,2,3,4,5].map(ri => {
                  const angle = (ri / 6) * 360;
                  const rad = Math.PI * 2 * ri / 6;
                  const r = 50;
                  return (
                    <div key={ri} style={{
                      position: 'absolute',
                      width: 7, height: 7,
                      borderRadius: '50%',
                      background: 'radial-gradient(circle at 35% 35%, #FF6060, #8B0000)',
                      border: '0.5px solid rgba(255,120,120,0.60)',
                      top: `calc(50% + ${Math.sin(rad) * 47}% - 3.5px)`,
                      left: `calc(50% + ${Math.cos(rad) * 47}% - 3.5px)`,
                      animation: 'gemPulse 1.8s ease-in-out infinite',
                      animationDelay: `${ri * 0.3}s`,
                    }} />
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Red carpet — perspective trapezoid */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'linear-gradient(to bottom, #5C0000 0%, #8B0000 15%, #B00000 40%, #CC1111 70%, #DD1111 100%)',
          clipPath: 'polygon(46.5% 32%, 53.5% 32%, 86% 100%, 14% 100%)',
          zIndex: 4,
        }} />
        {/* Carpet center sheen */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'linear-gradient(to bottom, transparent 30%, rgba(255,60,60,0.07) 60%, transparent 100%)',
          clipPath: 'polygon(49% 32%, 51% 32%, 52% 100%, 48% 100%)',
          zIndex: 5,
        }} />

        {/* Stanchions & sparkles */}
        {ROWS.map((row, i) => (
          <React.Fragment key={i}>
            {renderStanchion(row.lx, row.y, row)}
            {renderStanchion(row.rx, row.y, row)}
            {renderSparkles(row.lx, row.y, i * 0.28)}
            {renderSparkles(row.rx, row.y, i * 0.28 + 0.45)}
          </React.Fragment>
        ))}

        {/* Velvet ropes (SVG) */}
        <svg style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          zIndex: 11, pointerEvents: 'none',
        }} viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <filter id="rGlow">
              <feGaussianBlur stdDeviation="0.15" result="b" />
              <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>
          {ropes.map((d, i) => (
            <path key={i} d={d} stroke="#6B0000" strokeWidth="0.45" fill="none"
              strokeLinecap="round" filter="url(#rGlow)" />
          ))}
        </svg>

        {/* Camera flashes */}
        {FLASHES.map((f, i) => (
          <div key={i} style={{
            position: 'absolute',
            left: `${f.x}%`, top: `${f.y}%`,
            width: f.s, height: f.s, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,245,1) 0%, rgba(255,255,200,0.55) 45%, transparent 70%)',
            transform: 'translate(-50%,-50%)',
            animation: `camFlash ${2.2 + (i % 6) * 0.6}s ${f.d}s infinite`,
            zIndex: 6, pointerEvents: 'none',
          }} />
        ))}

        {/* ── Paparazzi silhouettes — left flank ─────────────────────────── */}
        {[
          { l:'0%',  t:'55%', hw:24, hh:24, sw:40, sh:22, op:0.75 },
          { l:'4%',  t:'64%', hw:28, hh:28, sw:46, sh:24, op:0.65 },
          { l:'1%',  t:'73%', hw:22, hh:22, sw:36, sh:20, op:0.55 },
          { l:'7%',  t:'58%', hw:26, hh:26, sw:42, sh:22, op:0.70 },
          { l:'2%',  t:'80%', hw:22, hh:22, sw:36, sh:20, op:0.48 },
          { l:'9%',  t:'67%', hw:22, hh:22, sw:36, sh:20, op:0.60 },
          { l:'5%',  t:'50%', hw:24, hh:24, sw:40, sh:20, op:0.62 },
          { l:'3%',  t:'86%', hw:20, hh:20, sw:32, sh:18, op:0.44 },
          { l:'10%', t:'62%', hw:22, hh:22, sw:36, sh:20, op:0.58 },
          { l:'0%',  t:'45%', hw:20, hh:20, sw:32, sh:18, op:0.50 },
          { l:'8%',  t:'88%', hw:22, hh:22, sw:36, sh:18, op:0.40 },
          { l:'11%', t:'72%', hw:22, hh:22, sw:36, sh:18, op:0.55 },
          { l:'6%',  t:'92%', hw:18, hh:18, sw:30, sh:16, op:0.35 },
        ].map((p, i) => (
          <div key={`lc-${i}`} style={{
            position:'absolute', left:p.l, top:p.t,
            zIndex:5, pointerEvents:'none', opacity:p.op,
            display:'flex', flexDirection:'column', alignItems:'center',
          }}>
            <div style={{ width:p.hw, height:p.hh, borderRadius:'50%', background:'#14100A' }} />
            <div style={{ width:p.sw, height:p.sh, marginTop:-2,
              background:'#14100A', borderRadius:'50% 50% 0 0 / 40% 40% 0 0' }} />
            {/* Camera arm */}
            <div style={{ position:'absolute', top: p.hh * 0.6, left:'60%',
              width: p.hw * 1.1, height: p.hh * 0.35, background:'#0C0A08',
              borderRadius:3, transform:'rotate(-20deg)' }} />
          </div>
        ))}

        {/* ── Paparazzi silhouettes — right flank ─────────────────────────── */}
        {[
          { r:'0%',  t:'55%', hw:24, hh:24, sw:40, sh:22, op:0.75 },
          { r:'4%',  t:'64%', hw:28, hh:28, sw:46, sh:24, op:0.65 },
          { r:'1%',  t:'73%', hw:22, hh:22, sw:36, sh:20, op:0.55 },
          { r:'7%',  t:'58%', hw:26, hh:26, sw:42, sh:22, op:0.70 },
          { r:'2%',  t:'80%', hw:22, hh:22, sw:36, sh:20, op:0.48 },
          { r:'9%',  t:'67%', hw:22, hh:22, sw:36, sh:20, op:0.60 },
          { r:'5%',  t:'50%', hw:24, hh:24, sw:40, sh:20, op:0.62 },
          { r:'3%',  t:'86%', hw:20, hh:20, sw:32, sh:18, op:0.44 },
          { r:'10%', t:'62%', hw:22, hh:22, sw:36, sh:20, op:0.58 },
          { r:'0%',  t:'45%', hw:20, hh:20, sw:32, sh:18, op:0.50 },
          { r:'8%',  t:'88%', hw:22, hh:22, sw:36, sh:18, op:0.40 },
          { r:'11%', t:'72%', hw:22, hh:22, sw:36, sh:18, op:0.55 },
          { r:'6%',  t:'92%', hw:18, hh:18, sw:30, sh:16, op:0.35 },
        ].map((p, i) => (
          <div key={`rc-${i}`} style={{
            position:'absolute', right:p.r, top:p.t,
            zIndex:5, pointerEvents:'none', opacity:p.op,
            display:'flex', flexDirection:'column', alignItems:'center',
          }}>
            <div style={{ width:p.hw, height:p.hh, borderRadius:'50%', background:'#14100A' }} />
            <div style={{ width:p.sw, height:p.sh, marginTop:-2,
              background:'#14100A', borderRadius:'50% 50% 0 0 / 40% 40% 0 0' }} />
            <div style={{ position:'absolute', top: p.hh * 0.6, right:'60%',
              width: p.hw * 1.1, height: p.hh * 0.35, background:'#0C0A08',
              borderRadius:3, transform:'rotate(20deg)' }} />
          </div>
        ))}

        {/* ── White limousine — left background ──────────────────────────── */}
        <div style={{
          position:'absolute', bottom:'30%', left:'1%',
          zIndex:7, pointerEvents:'none',
          width:'clamp(80px,16%,155px)',
          animation: 'limoGlow 4s ease-in-out infinite',
        }}>
          <div style={{
            width:'100%', height:22, position:'relative',
            background:'linear-gradient(180deg,#D8D0B8 0%,#A89E80 50%,#C8BE9C 100%)',
            borderRadius:'3px 12px 3px 3px',
            border:'1px solid rgba(212,168,83,0.45)',
          }}>
            {[0.18, 0.38, 0.58].map((pct, i) => (
              <div key={i} style={{
                position:'absolute', top:3, left:`${pct*100}%`,
                width:'12%', height:10,
                background:'rgba(50,80,120,0.45)',
                border:'0.5px solid rgba(212,168,83,0.30)',
                borderRadius:2,
              }} />
            ))}
            <div style={{
              position:'absolute', top:'48%', left:0, right:0, height:1,
              background:'linear-gradient(90deg,transparent,rgba(212,168,83,0.65) 20%,rgba(212,168,83,0.65) 80%,transparent)',
            }} />
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', padding:'0 8%', marginTop:2 }}>
            {[0,1].map(i => (
              <div key={i} style={{
                width:13, height:13, borderRadius:'50%',
                background:'radial-gradient(circle,#3A3A3A 28%,#0A0A0A 65%,#1E1E1E 100%)',
                border:'1.5px solid rgba(212,168,83,0.45)',
              }} />
            ))}
          </div>
          {/* Chauffeur */}
          <div style={{ position:'absolute', left:'-5%', bottom:14,
            display:'flex', flexDirection:'column', alignItems:'center' }}>
            <div style={{ width:9, height:9, borderRadius:'50%', background:'#141414' }} />
            <div style={{ width:12, height:24, marginTop:-1, background:'#141414',
              borderRadius:'2px 2px 0 0', border:'0.5px solid rgba(212,168,83,0.20)' }} />
          </div>
        </div>

        {/* ── Black limousine — right background ─────────────────────────── */}
        <div style={{
          position:'absolute', bottom:'30%', right:'1%',
          zIndex:7, pointerEvents:'none',
          width:'clamp(80px,16%,155px)',
          animation: 'limoGlow 4s ease-in-out 2s infinite',
        }}>
          <div style={{
            width:'100%', height:22, position:'relative',
            background:'linear-gradient(180deg,#1C1C1C 0%,#080808 55%,#161616 100%)',
            borderRadius:'3px 12px 3px 3px',
            border:'1px solid rgba(212,168,83,0.32)',
          }}>
            {[0.18, 0.38, 0.58].map((pct, i) => (
              <div key={i} style={{
                position:'absolute', top:3, left:`${pct*100}%`,
                width:'12%', height:10,
                background:'rgba(160,140,50,0.18)',
                border:'0.5px solid rgba(212,168,83,0.38)',
                borderRadius:2,
              }} />
            ))}
            <div style={{
              position:'absolute', top:'48%', left:0, right:0, height:1,
              background:'linear-gradient(90deg,transparent,rgba(212,168,83,0.55) 20%,rgba(212,168,83,0.55) 80%,transparent)',
            }} />
            {/* Open rear door */}
            <div style={{
              position:'absolute', top:-1, right:-1, bottom:-1, width:'17%',
              background:'#0A0A0A',
              border:'0.5px solid rgba(212,168,83,0.38)',
              borderRadius:'0 12px 3px 0',
              transform:'perspective(80px) rotateY(-22deg)',
              transformOrigin:'right center',
            }} />
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', padding:'0 8%', marginTop:2 }}>
            {[0,1].map(i => (
              <div key={i} style={{
                width:13, height:13, borderRadius:'50%',
                background:'radial-gradient(circle,#2A2A2A 28%,#0A0A0A 65%,#1E1E1E 100%)',
                border:'1.5px solid rgba(212,168,83,0.42)',
              }} />
            ))}
          </div>
          <div style={{ position:'absolute', right:'-5%', bottom:14,
            display:'flex', flexDirection:'column', alignItems:'center' }}>
            <div style={{ width:9, height:9, borderRadius:'50%', background:'#141414' }} />
            <div style={{ width:12, height:24, marginTop:-1, background:'#141414',
              borderRadius:'2px 2px 0 0', border:'0.5px solid rgba(212,168,83,0.20)' }} />
          </div>
        </div>

        {/* ── Security detail ─────────────────────────────────────────────── */}
        {[{ side:'left', pos:'13%' }, { side:'right', pos:'13%' }].map((s) => (
          <div key={s.side} style={{
            position:'absolute',
            ...(s.side==='left' ? { left:s.pos } : { right:s.pos }),
            bottom:'3%',
            zIndex:12, pointerEvents:'none',
            display:'flex', flexDirection:'column', alignItems:'center',
          }}>
            <div style={{ width:18, height:18, borderRadius:'50%', background:'#0A0A0A' }} />
            <div style={{ width:26, height:64, marginTop:-1, background:'#0A0A0A',
              borderRadius:'3px 3px 0 0', border:'0.5px solid rgba(212,168,83,0.22)' }} />
            <div style={{ position:'absolute', top:10,
              [s.side==='left' ? 'right' : 'left']: 0,
              width:4, height:4, borderRadius:'50%',
              background:'rgba(212,168,83,0.65)' }} />
          </div>
        ))}

        {/* ── Sponsor portraits on the carpet ─────────────────────────────── */}
        {CARPET_SPONSORS.map(renderSponsor)}

        {/* ── Title overlay ────────────────────────────────────────────────── */}
        <div style={{
          position: 'absolute', top: '2.5%', left: 0, right: 0,
          textAlign: 'center', zIndex: 50, pointerEvents: 'none',
        }}>
          <p style={{
            fontFamily: "'Cinzel',serif",
            fontSize: 'clamp(9px,1.8vw,14px)',
            letterSpacing: '5px', textTransform: 'uppercase',
            color: 'rgba(212,168,83,0.80)', margin: '0 0 4px',
            textShadow: '0 0 18px rgba(212,168,83,0.50)',
          }}>With Gratitude</p>
          <h1 style={{
            fontFamily: "'Cormorant Garamond',serif",
            fontSize: 'clamp(20px,4.5vw,52px)',
            fontWeight: 500, fontStyle: 'italic',
            color: 'rgba(255,228,100,0.96)',
            margin: 0, letterSpacing: '2px',
            textShadow: '0 2px 28px rgba(212,168,83,0.65), 0 0 55px rgba(212,168,83,0.28)',
          }}>Our Gracious Sponsors</h1>
        </div>

        {/* ── MEET ALL THE HERO SPONSORS button ──────────────────────────── */}
        <div
          data-testid="meet-sponsors-btn"
          onClick={() => cardsRef.current?.scrollIntoView({ behavior: 'smooth' })}
          style={{
            position: 'absolute', bottom: '3.5%', left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 50, cursor: 'pointer',
            padding: '10px 28px',
            background: 'linear-gradient(135deg, rgba(14,9,2,0.95), rgba(22,14,4,0.95))',
            border: '1.5px solid rgba(212,168,83,0.72)',
            borderRadius: 4,
            animation: 'heroBtnPulse 3s ease-in-out infinite',
            whiteSpace: 'nowrap',
          }}
        >
          <p style={{
            fontFamily: "'Cinzel',serif",
            fontSize: 'clamp(8px,1.4vw,13px)',
            fontWeight: 700,
            letterSpacing: '2.5px',
            color: 'rgba(255,220,80,0.95)',
            margin: 0, textTransform: 'uppercase',
            textShadow: '0 0 12px rgba(212,168,83,0.55)',
          }}>Meet All The Hero Sponsors</p>
        </div>

      </div>
      {/* ── End cinematic section ──────────────────────────────────────────── */}

      {/* ── SECTION 2: Sponsor cards ─────────────────────────────────────── */}
      {sponsors.length > 0 ? (
        <div
          ref={cardsRef}
          data-testid="sponsors-cards-section"
          style={{ padding: '52px 20px 72px', maxWidth: 820, margin: '0 auto' }}
        >
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <p style={{
              fontFamily: "'Cinzel',serif", fontSize: 10, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '4px',
              color: 'rgba(212,168,83,0.72)', margin: '0 0 12px',
            }}>Our Gracious Sponsors</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center', marginBottom: 16 }}>
              <div style={{ width: 44, height: 1, background: 'linear-gradient(90deg,transparent,rgba(212,168,83,0.60))' }} />
              <span style={{ color: 'rgba(212,168,83,0.80)', fontSize: 12 }}>✦</span>
              <div style={{ width: 44, height: 1, background: 'linear-gradient(90deg,rgba(212,168,83,0.60),transparent)' }} />
            </div>
            <p style={{
              fontFamily: "'Cormorant Garamond',serif",
              fontSize: 16, fontStyle: 'italic',
              color: 'rgba(255,255,255,0.68)',
              maxWidth: 420, margin: '0 auto', lineHeight: 1.65,
            }}>
              We are deeply grateful to those whose generosity made this evening possible.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 20 }}>
            {sponsors.map(s => <SponsorCard key={s.id} sponsor={s} />)}
          </div>
        </div>
      ) : (
        <div
          ref={cardsRef}
          data-testid="sponsors-empty-state"
          style={{ padding: '64px 24px', textAlign: 'center', maxWidth: 480, margin: '0 auto' }}
        >
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'rgba(200,170,60,0.10)',
            border: '1px solid rgba(200,170,60,0.30)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <Heart size={22} color="rgba(212,168,83,0.50)" />
          </div>
          <p style={{
            fontFamily: "'Cormorant Garamond',serif",
            fontSize: 18, fontStyle: 'italic',
            color: 'rgba(212,168,83,0.72)',
            margin: '0 0 8px', lineHeight: 1.6,
          }}>Sponsor details will be shared soon.</p>
          <p style={{
            fontFamily: "'Cormorant Garamond',serif",
            fontSize: 14, fontStyle: 'italic',
            color: 'rgba(255,255,255,0.42)', margin: 0,
          }}>We look forward to honoring those who made this evening possible.</p>
        </div>
      )}

      {/* Back button */}
      <button
        data-testid="sponsors-back-btn"
        onClick={() => window.history.length > 2 ? navigate(-1) : navigate('/')}
        style={{
          position: 'fixed', top: 18, left: 18, zIndex: 100,
          display: 'flex', alignItems: 'center', gap: 7,
          background: 'rgba(0,0,0,0.60)',
          border: '1px solid rgba(212,168,83,0.45)',
          borderRadius: 10, padding: '7px 15px',
          cursor: 'pointer', color: 'rgba(212,168,83,0.88)',
          fontFamily: "'Cinzel',serif", fontSize: 11, letterSpacing: '1px',
          backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
        }}
      >
        <ArrowLeft size={13} /> Back
      </button>

    </div>
  );
}
